<template>
  <div>
    <div class="card mt-3 bg-statusWarning">
      <div
        v-if="
          apparatusHasMultipleSubscriptions &&
            premiumSubscriptionApparatusMultiplePlans &&
            (isSingleApparatusSubscription(premiumSubscriptionApparatusMultiplePlans.subscriptionProductId) ||
              premiumSubscriptionApparatusMultiplePlans.apparatuses.length === 1)
        "
        class="card-body"
      >
        <div class="row mb-2">
          <div class="col">
            <span class="mb-0 pb-0" data-test-hook="pageTitle">
              <i class="far fa-triangle-exclamation pr-1" data-test-hook="titleIcon" />
              <span
                style="font-size:1rem; font-weight: bold;"
                data-test-hook="multiple-plans-account-needs-attention"
                >{{ $t('xYourAccountNeedsAttentionHeader') }}</span
              >
            </span>
          </div>
        </div>
        <div class="row mb-2 ml-2">
          <div class="col">
            <span data-test-hook="apparatus-has-multiple-subs-body-text">
              {{
                $t('xApparatusHasMultipleSubsOnMobileBodyText1', {
                  0: apparatusHasMultipleSubscriptions.name,
                  1: apparatusHasMultipleSubscriptions.preferredDealerName,
                  2: premiumSubscriptionApparatusMultiplePlans.subscriptionProductName,
                  3: $moment(premiumSubscriptionApparatusMultiplePlans.subscriptionRenewalDate).format($t('xShortDate'))
                })
              }}
            </span>
          </div>
        </div>
        <div class="row mb-1 ml-2">
          <div class="col">
            <button
              type="button"
              class="btn btn-warning btn-upgrade"
              @click="$emit('openCancelPlanMultiplePlansModal')"
              data-test-hook="openCancelPlanMultiplePlansBtn"
            >
              {{ $t('xCancelPlanLowercase') }}
            </button>
          </div>
        </div>
      </div>
      <div
        v-else-if="
          apparatusHasMultipleSubscriptions &&
            premiumSubscriptionApparatusMultiplePlans &&
            isHomePremiumSubscription(premiumSubscriptionApparatusMultiplePlans.subscriptionProductId)
        "
        class="card-body"
      >
        <div class="row mb-2">
          <div class="col">
            <span class="mb-0 pb-0" data-test-hook="pageTitle">
              <i class="far fa-triangle-exclamation pr-1" data-test-hook="titleIcon" />
              <span
                style="font-size:1rem; font-weight: bold;"
                data-test-hook="multiple-plans-account-needs-attention"
                >{{ $t('xYourAccountNeedsAttentionHeader') }}</span
              >
            </span>
          </div>
        </div>
        <div class="row mb-2 ml-2">
          <div class="col">
            <span data-test-hook="home-premium-apparatus-has-multiple-subs-body-text">
              {{
                $t('xAlreadyManagedPleaseRemoveApparatus', {
                  0: apparatusHasMultipleSubscriptions.name,
                  1: apparatusHasMultipleSubscriptions.preferredDealerName,
                  2: getApparatusTypeAsString,
                  3: premiumSubscriptionApparatusMultiplePlans.subscriptionProductName
                })
              }}
            </span>
          </div>
        </div>
        <div class="row mb-1 ml-2">
          <div class="col">
            <button
              type="button"
              class="btn btn-warning btn-upgrade"
              @click="$emit('openRemoveApparatusMultiplePlansModal')"
              data-test-hook="openRemoveApparatusMultiplePlansBtn"
            >
              {{ $t('xRemoveFromPlan') }}
            </button>
          </div>
        </div>
      </div>
      <div v-else class="card-body">
        <div class="row mb-2">
          <div class="col">
            <span class="mb-0 pb-0" data-test-hook="pageTitle">
              <i class="far fa-triangle-exclamation pr-1" data-test-hook="titleIcon" />
              <span style="font-size:1rem; font-weight: bold;" data-test-hook="subscription-plan-details">{{
                $t('xYourAccountNeedsAttentionHeader')
              }}</span>
            </span>
          </div>
        </div>
        <div class="row mb-2 ml-2">
          <div class="col">
            <span v-if="isOverLimit()" data-test-hook="plan-cost-header-text-two-four-limit">
              {{ $t('xHomePremium2And4LimitText') }}
            </span>
            <span
              v-else-if="hasPartiallyEnrolledTank(allValidApparatusesOnAccount)"
              data-test-hook="finish-setup-fuel-monitor"
            >
              {{ $t('xFinishSettingUpFuelMonitor') }}
            </span>
            <span v-else data-test-hook="upgrade-update-banner-header-text">
              {{ getUpgradeUpdateBannerText() }}
            </span>
          </div>
        </div>
        <div v-if="isOverLimit()" class="row mb-1 ml-2">
          <div class="col">
            <button
              type="button"
              class="btn btn-warning btn-upgrade"
              @click="openLearnMore()"
              data-test-hook="openLearnMoreBtn"
            >
              {{ $t('xLearnMore') }}
            </button>
          </div>
        </div>
        <div v-else-if="hasPartiallyEnrolledTank(allValidApparatusesOnAccount)" class="row mb-1 ml-2">
          <div class="col">
            <button
              type="button"
              class="btn btn-warning btn-upgrade"
              @click="openTankEnrollmentFlow()"
              data-test-hook="openLearnMoreBtn"
            >
              {{ $t('xFinishSetup') }}
            </button>
          </div>
        </div>
        <div v-else-if="isDifferentPlatform()" class="ml-4">
          <div class="mt-4" v-if="isAppleSub()">
            <div class="row">
              <div class="col">
                <span data-test-hook="manage-plan-text">
                  {{ $t('xManagePlanOnApple') }}
                </span>
              </div>
            </div>
            <div class="row mb-3">
              <a href="https://account.apple.com/account/manage/section/subscriptions" target="_blank">
                <span data-test-hook="manage-plan-text" class="ml-3">
                  {{ 'https://account.apple.com/account/manage/section/subscriptions.' }}
                </span>
              </a>
            </div>
          </div>
          <div class="mt-4" v-else>
            <div class="row">
              <div class="col">
                <span data-test-hook="manage-plan-text">
                  {{ $t('xManagePlanOnGoogle') }}
                </span>
              </div>
            </div>
            <div class="row mb-3">
              <a href="https://play.google.com/store/account/subscriptions" target="_blank">
                <span data-test-hook="manage-plan-text" class="ml-3">
                  {{ 'https://play.google.com/store/account/subscriptions.' }}
                </span>
              </a>
            </div>
          </div>
        </div>
        <div v-else class="row mb-1 ml-2">
          <div class="col">
            <button
              type="button"
              class="btn btn-warning btn-upgrade"
              @click="openSubscribePremium()"
              data-test-hook="openSubscribePremiumMonitoringBtn"
              :disabled="disableUpgradeButton"
            >
              {{ getUpgradeUpdateButtonLabel() }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <BeforeYouUpgradeModal
      :apparatusesIncludedInUpgrade="apparatusesNeedsAttention"
      @openSubscriptionModal="openSubscriptionModal"
    />
    <PendingPlanChangeModal
      modalId="pending-plan-change-modal-settingsSubscriptions"
      @onYesContinueClick="openSubscriptionModal"
    />
    <PendingPlanCancelModal
      modalId="pending-plan-cancel-modal-settingsSubscriptions"
      :planCanceledDate="pendingCancelRenewalDate"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import * as Constants from '@/constants'
import BeforeYouUpgradeModal from './BeforeYouUpgradeModal.vue'
import PendingPlanChangeModal from '../../Common/Subscriptions/PendingPlanChangeModal.vue'
import PendingPlanCancelModal from '../../Common/Subscriptions/PendingPlanCancelModal.vue'
import { hasPartiallyEnrolledTank, tankHasAllProperties } from '../../../utils/apparatus.js'
const subscriptionViews = {
  EXISTING_SUB: 'subscription-existing'
}

/**
 * The AccountNeedsAttentionBanner shows on the SubscriptionSettings details page when there is an issue with the account/subscription.
 */
export default {
  components: {
    BeforeYouUpgradeModal,
    PendingPlanChangeModal,
    PendingPlanCancelModal
  },

  name: 'AccountNeedsAttentionBanner',
  props: [
    'apparatusesNeedsAttention',
    'apparatusHasMultipleSubscriptions',
    'premiumSubscriptionApparatusMultiplePlans',
    'allValidApparatusesOnAccount',
    'disableUpgradeButton',
    'currentSubscription',
    'subscriptionDetails',
    'productsOnBasicSubscriptionOrNotInPlan'
  ],
  data() {
    return {
      numberOfHsbApparatusesWithAttention: 0,
      numberOfFMApparatusesWithAttention: 0,
      numberOfValidHsbApparatuses: 0,
      numberOfValidFMApparatuses: 0,
      numberOfHsbInSubscription: 0,
      numberOfFMInSubscription: 0,
      activeView: '',
      pendingCancelRenewalDate: null
    }
  },
  computed: {
    ...mapState({
      accountProfile: state => state.account.accountProfile
    }),
    getApparatusTypeAsString() {
      switch (this.apparatusHasMultipleSubscriptions?.type) {
        case Constants.HsbApparatusType:
          return this.$t('xGeneratorLowercase')
        case Constants.FuelTankApparatusType:
          return this.$t('xFuelMonitorLowercase')
        default:
          return ''
      }
    }
  },
  methods: {
    getUpgradeUpdateButtonLabel() {
      // No existing plan, user needs to upgrade to premium.
      if (!this.hasExistingPlanOnAccount()) {
        return this.$t('xUpgradePremium')
      }

      // Existing plan has room, user can update/add to current plan.
      if (this.planHasRoom()) {
        return this.$t('xUpdateNow')
      }

      // Existing plan is full, user needs to upgrade to larger plan.
      return this.$t('xUpgradeNow')
    },
    getUpgradeUpdateBannerText() {
      // Determine if we're dealing with a single product or multiple.
      const isSingular = this.apparatusesNeedsAttention?.length === 1 || this.hasProductNotInPlan()

      const upgradeText = isSingular
        ? this.$t('xYouHaveAProductNotIncludedInAPlanUpgrade')
        : this.$t('xYouHaveProductsNotIncludedInAPlanUpgrade')

      const updateText = isSingular
        ? this.$t('xYouHaveAProductNotIncludedInAPlanUpdate')
        : this.$t('xYouHaveProductsNotIncludedInAPlanUpdate')

      // No existing plan, user needs to upgrade to premium.
      if (!this.hasExistingPlanOnAccount()) {
        return upgradeText
      }

      // Existing plan has room, user can update/add to current plan.
      if (this.planHasRoom()) {
        return updateText
      }

      // Existing plan is full, user needs to upgrade to larger plan.
      return upgradeText
    },
    openSubscribePremium() {
      this.$appInsights.trackEvent({
        name: 'click-account-needs-attention-upgrade-premium',
        properties: {}
      })

      if (this.hasPendingProductChange()) {
        this.openModal('pending-plan-change-modal-settingsSubscriptions')
      } else if (this.hasPendingCancelPlan()) {
        this.openModal('pending-plan-cancel-modal-settingsSubscriptions')
      } else if (this.apparatusesNeedsAttention?.length > 1) {
        this.openModal('before-you-upgrade-modal')
      } else if (this.apparatusesNeedsAttention?.length === 1) {
        this.openSubscriptionModal()
      }
    },

    async openSubscriptionModal() {
      this.$bvModal.show('upgrade-subscription-modal')
    },

    hasPendingProductChange() {
      return this.currentSubscription?.subscriptionStatus === Constants.SubscriptionDetailPendingProductChange
    },

    hasPendingCancelPlan() {
      const iapPendingCancelSubscription = this.subscriptionDetails?.subscriptions?.find(
        subscription => subscription.status === Constants.SubscriptionDetailPendingCancel
      )
      const legacyPendingCancelSubscription = this.subscriptionDetails?.legacyBillingProducts?.products.find(
        product => product.status === Constants.SubscriptionPendingCancellation
      )
      if (iapPendingCancelSubscription) this.pendingCancelRenewalDate = iapPendingCancelSubscription.renewalDate
      else if (legacyPendingCancelSubscription)
        this.pendingCancelRenewalDate = legacyPendingCancelSubscription.nextBillingDate
      return iapPendingCancelSubscription || legacyPendingCancelSubscription
    },

    isDifferentPlatform() {
      if (this.currentSubscription) {
        return this.currentSubscription?.subscriptionStore !== Constants.WebSubscriptionStore
      }
      return false
    },

    isAppleSub() {
      return this.currentSubscription?.subscriptionStore === Constants.AppleSubscriptionStore
    },

    hasExistingPlanOnAccount() {
      if (this.currentSubscription && !this.requireUpgrade()) {
        this.activeView = subscriptionViews.EXISTING_SUB
      }

      if (this.activeView && this.activeView === subscriptionViews.EXISTING_SUB) {
        return true
      }
      return false
    },

    /** Returns true if the existing plan on the account has room */
    planHasRoom() {
      const planType = this.currentSubscription?.subscriptionProductId
      // Single HSB
      if (
        planType === Constants.SingleGeneratorMonthlyProductId ||
        planType === Constants.SingleGeneratorYearlyProductId
      ) {
        return false
        // Single FM
      } else if (
        planType === Constants.SingleFuelMonitorMonthlyProductId ||
        planType === Constants.SingleFuelMonitorYearlyProductId
      ) {
        return false
        // Single home
      } else if (
        planType === Constants.HomePremiumOneYearlyProductId ||
        planType === Constants.HomePremiumOneMonthlyProductId
      ) {
        // Checks to make sure there is room in the plan
        if (
          this.numberOfHsbInSubscription + this.numberOfHsbApparatusesWithAttention <= 1 &&
          this.numberOfFMInSubscription + this.numberOfFMApparatusesWithAttention <= 2
        ) {
          return true
        }
        return false
        // Multi home
      } else if (
        planType === Constants.HomePremiumMultiHomeYearlyProductId ||
        planType === Constants.HomePremiumMultiHomeMonthlyProductId
      ) {
        // Checks to make sure there is room in the plan
        if (
          this.numberOfHsbInSubscription + this.numberOfHsbApparatusesWithAttention <= 2 &&
          this.numberOfFMInSubscription + this.numberOfFMApparatusesWithAttention <= 4
        ) {
          return true
        }
        return false
      }
      // Default return value
      return false
    },

    openLearnMore() {
      this.$appInsights.trackEvent({
        name: 'click-account-needs-attention-learn-more',
        properties: {}
      })
      window.open(Constants.LimitReachedHelpArticleUrl)
    },

    openTankEnrollmentFlow() {
      // Find the first partially enrolled tank
      const partiallyEnrolledTank = this.allValidApparatusesOnAccount.find(
        apparatus => apparatus.type === Constants.FuelTankApparatusType && !tankHasAllProperties(apparatus)
      )

      if (partiallyEnrolledTank) {
        // Navigate to dashboard with apparatus params to trigger enrollment flow
        // Dashboard watches for apparatusType and opens tank-enrollment-step-3 modal
        this.$router.push({
          name: 'beginEnrollmentFlow',
          params: {
            apparatusType: partiallyEnrolledTank.type,
            apparatusId: partiallyEnrolledTank.apparatusId
          }
        })
      }
    },
    /** Returns true if the account is over the apparatus limit */
    isOverLimit() {
      return (
        this.numberOfHsbApparatusesWithAttention > 2 ||
        this.numberOfFMApparatusesWithAttention > 4 ||
        this.numberOfValidHsbApparatuses > 2 ||
        this.numberOfValidFMApparatuses > 4
      )
    },

    /** Returns true if there is no sub on the account or the account is over the apparatus count */
    requireUpgrade() {
      if (!this.currentSubscription) {
        return true
      }

      return this.isOverLimit()
    },

    /** Returns true if there is 1 product that is on Basic and there is a non-cancelled, modern subscription on the account. */
    hasProductNotInPlan() {
      if (!this.productsOnBasicSubscriptionOrNotInPlan.length === 0 || !this.currentSubscription) {
        return false
      }
      if (
        this.productsOnBasicSubscriptionOrNotInPlan.length === 1 &&
        this.currentSubscription.subscriptionStatus !== Constants.SubscriptionDetailCanceled
      ) {
        return true
      } else {
        return false
      }
    },
    hasPartiallyEnrolledTank,
    tankHasAllProperties
  },
  mounted() {
    this.numberOfHsbApparatusesWithAttention = this.apparatusesNeedsAttention.filter(
      x => x.type === Constants.HsbApparatusType
    ).length
    this.numberOfFMApparatusesWithAttention = this.apparatusesNeedsAttention.filter(
      x => x.type === Constants.FuelTankApparatusType
    ).length
    this.numberOfValidHsbApparatuses = this.allValidApparatusesOnAccount.filter(
      x => x.type === Constants.HsbApparatusType && !x.isDealerManaged
    ).length
    this.numberOfValidFMApparatuses = this.allValidApparatusesOnAccount.filter(
      x => x.type === Constants.FuelTankApparatusType && !x.isDealerManaged
    ).length
    this.numberOfHsbInSubscription = this.currentSubscription?.apparatuses.filter(
      x => x.type === Constants.HsbApparatusType
    ).length
    this.numberOfFMInSubscription = this.currentSubscription?.apparatuses.filter(
      x => x.type === Constants.FuelTankApparatusType
    ).length
  }
}
</script>

<style scoped>
.btn-upgrade {
  background-color: var(--carbon-900) !important;
}
.btn-upgrade:hover {
  background-color: var(--carbon-800) !important;
}
.btn-upgrade:pressed {
  background-color: var(--black) !important;
}
</style>
